import { InvoiceLineItemUpsertDto } from '../../../invoices/dto/invoice-line-item-upsert.dto';
import { Injectable } from '@angular/core';
import { IRevenueItemService } from './irevenue-item.service';
import { RevenueItem } from './revenue-item.model';
import { ApiService, ApiCacheOptions, PagedResultGeneric } from '../../../shared/index';
import { RevenueItemSearchDto } from './dto/revenue-item-search.dto';
import { RevenueItemTaxItemUpsertDto } from './dto/revenue-item-tax-item-upsert.dto';

@Injectable()
export class RevenueItemService implements IRevenueItemService {
  public _apiService: ApiService;
  private _cacheOptions: ApiCacheOptions;

  public constructor(apiService: ApiService) {
    this._apiService = apiService;
    this._cacheOptions = this._apiService.getDefaultApiCacheOptionsForInfrequentlyChangedData();
  }

  public searchRevenueItems(dto: RevenueItemSearchDto, forceRefresh?: boolean): Promise<PagedResultGeneric<RevenueItem>> {
    const cacheOptions = forceRefresh ? this._apiService.getDefaultApiCacheOptionsForNoCache() : null;
    return this._apiService.getPaged('RevenueItems', dto, cacheOptions, true);
  }

  public create(dto: RevenueItem): Promise<number> {
    return this._apiService.post('RevenueItems', dto);
  }

  public update(dto: RevenueItem): Promise<void> {
    return this._apiService.put(`RevenueItem/${dto.ItemNum}`, dto);
  }

  public delete(id: number): Promise<void> {
    return this._apiService.delete(`RevenueItem/${id}`);
  }

  public getAllRevenueItems(): Promise<RevenueItem[]> {
    return this._apiService.get(`RevenueItems/AllRevenueItems`);
  }

  public getTaxLineItems(item: RevenueItem, amount = 0, taxLocality = ''): Promise<Array<InvoiceLineItemUpsertDto>> {
    const noCacheOptions = this._apiService.getDefaultApiCacheOptionsForNoCache();
    return this._apiService.get(`RevenueItem/${item.ItemNum}/Tax/LineItems?amount=${amount}&taxLocality=${taxLocality}`, noCacheOptions);
  }

  public getRevenueItemTaxItemUpsertDto(itemNum: number): Promise<RevenueItemTaxItemUpsertDto> {
    return this._apiService.get(
      `RevenueItem/${itemNum}/RevenueItemTaxItemUpsert`,
      this._apiService.getDefaultApiCacheOptionsForNoCache(),
      true
    );
  }

  public upsertRevenueItemTaxItems(dto: RevenueItemTaxItemUpsertDto): Promise<void> {
    return this._apiService.post(`RevenueItem/${dto.ParentItemNum}/RevenueItemTaxItemUpsert`, dto, true);
  }

  /*
   Gets the tax amount for the given item number and amount
  */
  public getItemTaxAmount(itemNum: number, amount): Promise<number> {
    const url = `RevenueItem/${itemNum}/TaxAmount?amount=${amount}`;
    return this._apiService.get(url);
  }

  public getRevenueItems = (): Promise<Array<RevenueItem>> => {
    return this._apiService.get('RevenueItems/ActiveRevenueItems', this._cacheOptions).then((data: Array<RevenueItem>) => {
      return Promise.resolve(data);
    });
  };

  public getRevenueItem = (revenueItemId: number): Promise<RevenueItem> => {
    return this._apiService.get('RevenueItem/' + revenueItemId.toString(), this._cacheOptions, true).then((data: RevenueItem) => {
      return Promise.resolve(data);
    });
  };

  public getRevenueItemsForAccountingPackage = (accountingPackageId: number): Promise<Array<RevenueItem>> => {
    return this._apiService
      .get('RevenueItems/AccountingPackage/' + accountingPackageId.toString(), this._cacheOptions)
      .then((data: Array<RevenueItem>) => {
        return Promise.resolve(data);
      });
  };

  public getRevenueItemsForPaymentType = (paymentTypeId: number): Promise<Array<RevenueItem>> => {
    return this._apiService
      .get('RevenueItems/PaymentType/' + paymentTypeId.toString(), this._cacheOptions)
      .then((data: Array<RevenueItem>) => {
        return Promise.resolve(data);
      });
  };

  public revenueItemHasArcs = (revenueItemNum: number): Promise<boolean> => {
    return this._apiService.get('AutoRevCalcs?ItemNum=' + revenueItemNum.toString(), this._cacheOptions).then((data: any) => {
      return data.TotalCount > 0;
    });
  };
}
